<template>
  <div class="converter-container">
    <h2 class="mb-4">VCX -> AED Converter</h2>
    <p class="mb-6">
      Upload a <code>.vcx</code> (Vehicle Coding Export) file and this tool will
      generate an <code>.aed</code> document for every ECU it contains. You can
      download each AED separately or grab a ZIP with all of them.
    </p>

    <v-file-input
      label="Select VCX file"
      accept=".vcx,.xml,text/xml"
      show-size
      density="comfortable"
      prepend-icon="mdi-file-upload"
      @update:model-value="handleFile"
    />

    <v-alert
      v-if="error"
      type="error"
      class="mt-4"
      border="start"
      variant="tonal"
    >
      {{ error }}
    </v-alert>

    <v-progress-linear
      v-if="isProcessing"
      indeterminate
      color="primary"
      class="mt-4"
    />

    <v-alert
      v-if="modules.length > 1"
      type="info"
      class="mt-4"
      border="start"
      variant="tonal"
    >
      Detected {{ modules.length }} ECU modules. Use the download icon in each
      row for single AEDs or the ZIP button for the entire batch.
    </v-alert>

    <v-card v-if="metadata" class="mt-6" variant="outlined">
      <v-card-title>Detected Metadata</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="meta-label">Title</div>
            <div class="meta-value">{{ metadata.title }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Kernel Version</div>
            <div class="meta-value">{{ metadata.productVersion }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Kernel Type</div>
            <div class="meta-value">{{ metadata.creator }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Creation Date</div>
            <div class="meta-value">{{ metadata.date }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Creation Time</div>
            <div class="meta-value">{{ metadata.time }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Modules</div>
            <div class="meta-value">{{ modules.length }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="modules.length" class="mt-6" variant="outlined">
      <v-card-title>Modules ({{ modules.length }})</v-card-title>
      <v-card-text>
        <v-list density="comfortable">
          <v-list-item
            v-for="(module, index) in modules"
            :key="module.fileName"
            :active="selectedModuleIndex === index"
            @click="selectModule(index)"
          >
            <template #prepend>
              <v-icon>mdi-chip</v-icon>
            </template>
            <v-list-item-title>{{ module.name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ module.records }} coding entries - {{ module.fileName }}.aed
            </v-list-item-subtitle>
            <template #append>
              <v-btn
                icon="mdi-download"
                variant="text"
                :aria-label="`Download ${module.fileName}.aed`"
                @click.stop="downloadModule(module)"
              ></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-textarea
      v-model="aedContent"
      class="mt-6"
      rows="12"
      auto-grow
      :label="
        selectedModule
          ? `AED preview (${selectedModule.fileName}.aed)`
          : 'AED preview'
      "
      readonly
      outlined
    />

    <div class="action-row mt-4">
      <v-btn
        color="primary"
        :disabled="!modules.length"
        prepend-icon="mdi-zip-box"
        @click="downloadZip"
      >
        Download ZIP
      </v-btn>
      <v-btn class="ml-4" variant="tonal" color="secondary" @click="resetForm">
        Reset
      </v-btn>
    </div>
  </div>
</template>

<script>
import JSZip from "jszip";

export default {
  name: "VcxToAed",
  data() {
    return {
      error: "",
      isProcessing: false,
      vcxContent: "",
      metadata: null,
      modules: [],
      selectedModuleIndex: 0,
      aedContent: "",
      originalFileName: "",
    };
  },
  computed: {
    selectedModule() {
      return this.modules[this.selectedModuleIndex] || null;
    },
  },
  methods: {
    handleFile(file) {
      this.resetOutput();
      const selectedFile = Array.isArray(file) ? file[0] : file;

      if (!selectedFile) {
        return;
      }

      this.originalFileName = selectedFile.name || "converted.vcx";
      const reader = new FileReader();
      reader.onload = (event) => {
        this.vcxContent = event.target.result;
        this.convertVcxToAed();
      };
      reader.onerror = () => {
        this.error = "Unable to read the provided file.";
      };
      reader.readAsText(selectedFile);
    },
    resetOutput() {
      this.error = "";
      this.metadata = null;
      this.modules = [];
      this.selectedModuleIndex = 0;
      this.aedContent = "";
    },
    resetForm() {
      this.resetOutput();
      this.vcxContent = "";
      this.originalFileName = "";
    },
    convertVcxToAed() {
      if (!this.vcxContent) {
        this.error = "No VCX content provided.";
        return;
      }

      this.isProcessing = true;

      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
          this.vcxContent,
          "application/xml"
        );

        if (xmlDoc.querySelector("parsererror")) {
          throw new Error("Unable to parse VCX XML. Please verify the file.");
        }

        const generalMetadata = this.extractMetadata(xmlDoc);
        const modules = this.extractModules(xmlDoc, generalMetadata);

        if (!modules.length) {
          throw new Error("No ECU modules with coding data were found.");
        }

        this.modules = modules;
        this.selectedModuleIndex = 0;
        this.aedContent = modules[0].content;
        this.metadata = generalMetadata;
      } catch (err) {
        this.error =
          err?.message || "Unexpected error while converting VCX file.";
      } finally {
        this.isProcessing = false;
      }
    },
    extractMetadata(xmlDoc) {
      const title =
        xmlDoc.querySelector("General > Comment")?.textContent?.trim() ||
        "VCX Export";
      const kernelVersion =
        xmlDoc
          .querySelector("General > Kernel > KernelVersion")
          ?.textContent?.trim() || "Unknown";
      const kernelType =
        xmlDoc
          .querySelector("General > Kernel > KernelType")
          ?.textContent?.trim() || "Softing";
      const creationTime =
        xmlDoc.querySelector("General > CreationTime")?.textContent?.trim() ||
        "";

      const parsedDate = creationTime ? new Date(creationTime) : new Date();
      const pad = (value) => value.toString().padStart(2, "0");
      const day = pad(parsedDate.getDate());
      const month = pad(parsedDate.getMonth() + 1);
      const year = parsedDate.getFullYear();
      const hours = pad(parsedDate.getHours());
      const minutes = pad(parsedDate.getMinutes());
      const seconds = pad(parsedDate.getSeconds());

      return {
        title,
        productVersion: kernelVersion,
        creator: kernelType,
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}:${seconds}`,
        comment: title,
        author: "NPCODE",
      };
    },
    extractModules(xmlDoc, metadata) {
      const moduleNodes = Array.from(
        xmlDoc.querySelectorAll("FlashCodingElement")
      );
      const modules = [];

      moduleNodes.forEach((node, index) => {
        const codingRecords = Array.from(
          node.querySelectorAll("CodingDataInformation")
        );

        if (!codingRecords.length) {
          return;
        }

        const baseVariant =
          node.querySelector("ECUBaseVariantName")?.textContent?.trim() ||
          `Module_${index + 1}`;

        const recordLines = codingRecords.map((record) => {
          const serviceName =
            record.querySelector("CodingServiceName")?.textContent?.trim() ||
            "";
          const codingString =
            record.querySelector("CodingString")?.textContent?.trim() || "";

          if (!serviceName) {
            throw new Error(
              "Encountered a coding record without a ServiceName."
            );
          }

          const key = this.toAedKey(serviceName);
          const formattedValue = this.formatCodingString(codingString);
          return `${key}=${formattedValue}`;
        });

        modules.push({
          name: baseVariant,
          baseVariant,
          records: codingRecords.length,
          fileName: this.toSafeFileName(baseVariant, index),
          content: `${this.buildHeader(metadata, {
            baseVariant,
            records: codingRecords.length,
          })}\r\n${recordLines.join("\r\n")}\r\n`,
        });
      });

      return modules;
    },
    buildHeader(meta, moduleInfo) {
      return [
        "[Description]",
        `Title=${meta.title}`,
        `ProductVersion=${meta.productVersion}`,
        `Date=${meta.date}`,
        `Time=${meta.time}`,
        `Author=${meta.author}`,
        `Comment=${meta.comment}`,
        "NoOfConfigurations=1",
        "[Configuration_001]",
        `ECUBaseVariantName=${moduleInfo.baseVariant}`,
        `NoOfDomains=${moduleInfo.records}`,
        `Title=${moduleInfo.baseVariant}`,
        "Comment=",
      ].join("\r\n");
    },
    toAedKey(serviceName) {
      let baseName = serviceName
        .replace(/_(Read|Write)(_[0-9]+)?_Dump$/i, "")
        .replace(/_(Read|Write)$/i, "")
        .replace(/_Dump$/i, "");

      if (!baseName) {
        baseName = serviceName;
      }

      return `VCD_${baseName.replace(/[^A-Za-z0-9_]/g, "_")}`;
    },
    formatCodingString(rawValue) {
      if (!rawValue) {
        return "";
      }

      const sanitized = rawValue.replace(/[^a-fA-F0-9]/g, "").toUpperCase();
      if (!sanitized) {
        return "";
      }

      const evenLength =
        sanitized.length % 2 === 0 ? sanitized : `${sanitized}0`;
      const pairs = evenLength.match(/.{1,2}/g) || [];
      return pairs.join(",");
    },
    selectModule(index) {
      this.selectedModuleIndex = index;
      this.aedContent = this.modules[index]?.content ?? "";
    },
    toSafeFileName(name, index) {
      const sanitized = name
        .replace(/[^A-Za-z0-9-_]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
      return sanitized || `module_${index + 1}`;
    },
    async downloadZip() {
      if (!this.modules.length) {
        return;
      }

      try {
        const zip = new JSZip();
        this.modules.forEach((module) => {
          zip.file(`${module.fileName}.aed`, module.content);
        });

        const baseName =
          this.originalFileName?.replace(/\.(vcx|xml)$/i, "") || "converted";
        const blob = await zip.generateAsync({ type: "blob" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${baseName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.error = "Failed to create the ZIP archive.";
      }
    },
    downloadModule(module) {
      if (!module) {
        return;
      }

      const blob = new Blob([module.content], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${module.fileName}.aed`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.converter-container {
  padding: 24px;
}

.meta-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.meta-value {
  font-weight: 600;
  font-size: 1rem;
}

.action-row {
  display: flex;
  align-items: center;
}
</style>
